/**
 * Kundali Calculations
 * Astronomical calculations for Vedic astrology
 */

import { NAKSHATRA_DATA, RASHI_DATA, getNakshatraSpan, getPadaSpan } from '../data/nakshatraData';
import {
  SIGNS,
  PLANETS,
  HOUSE_LORDS,
  HOUSE_SIGNIFICANCE,
  EXALTATION_SIGNS,
  DEBILITATION_SIGNS,
  OWN_SIGNS,
  PLANET_REMEDIES,
  BENEFIC_PLANETS,
  MALEFIC_PLANETS,
  BENEFIC_HOUSES,
  DAY_LORDS,
  DAY_LORDS_HI,
  DAY_NAMES,
  DAY_NAMES_HI,
  MANGLIK_HOUSES
} from '../data/kundaliConstants';

/**
 * Calculate Julian Day Number from date and time
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute
 * @param {number} timezone - Timezone offset from UTC
 * @returns {number} Julian Day Number
 */
export const getJulianDay = (year, month, day, hour = 0, minute = 0, timezone = 5.5) => {
  // Convert to UTC from local timezone
  let utcHour = hour - timezone;
  let utcDay = day;
  let utcMonth = month;
  let utcYear = year;

  if (utcHour < 0) {
    utcHour += 24;
    utcDay -= 1;
  }
  if (utcHour >= 24) {
    utcHour -= 24;
    utcDay += 1;
  }

  // Handle month boundaries
  if (utcDay < 1) {
    utcMonth -= 1;
    if (utcMonth < 1) {
      utcMonth = 12;
      utcYear -= 1;
    }
    const daysInPrevMonth = new Date(utcYear, utcMonth, 0).getDate();
    utcDay = daysInPrevMonth;
  }

  const dayFraction = (utcHour + minute / 60) / 24;

  let y = utcYear;
  let m = utcMonth;

  if (m <= 2) {
    y -= 1;
    m += 12;
  }

  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);

  const JD = Math.floor(365.25 * (y + 4716)) +
             Math.floor(30.6001 * (m + 1)) +
             utcDay + dayFraction + B - 1524.5;

  return JD;
};

/**
 * Calculate Ayanamsa (Lahiri) for sidereal calculations
 * @param {number} jd - Julian Day
 * @returns {number} Ayanamsa in degrees
 */
export const getLahiriAyanamsa = (jd) => {
  // Lahiri Ayanamsa calculation
  // Reference: Ayanamsa on Jan 1, 2000 (JD 2451545.0) was approximately 23°51'
  const T = (jd - 2451545.0) / 36525; // Centuries from J2000.0

  // Lahiri ayanamsa formula (approximate)
  const ayanamsa = 23.85 + (0.0137 * T * 100); // degrees

  return ayanamsa;
};

/**
 * Accurate Moon longitude calculation using lunar orbital elements
 * @param {number} jd - Julian Day
 * @returns {number} Moon longitude in degrees
 */
export const calculateMoonLongitude = (jd) => {
  // Days from J2000.0 epoch (Jan 1, 2000, 12:00 UT)
  const d = jd - 2451545.0;

  // Moon's orbital elements (low precision formula - good for nakshatra)
  // Mean longitude of the Moon
  let L = 218.316 + 13.176396 * d;

  // Mean anomaly of the Moon
  const M = 134.963 + 13.064993 * d;

  // Mean anomaly of the Sun
  const Ms = 357.529 + 0.985600 * d;

  // Moon's argument of latitude
  const F = 93.272 + 13.229350 * d;

  // Mean elongation of the Moon
  const D = 297.850 + 12.190749 * d;

  // Convert to radians
  const Mrad = M * Math.PI / 180;
  const Msrad = Ms * Math.PI / 180;
  const Frad = F * Math.PI / 180;
  const Drad = D * Math.PI / 180;

  // Longitude corrections (main perturbations)
  let dL = 0;
  dL += 6.289 * Math.sin(Mrad);                    // Equation of center
  dL += 1.274 * Math.sin(2 * Drad - Mrad);         // Evection
  dL += 0.658 * Math.sin(2 * Drad);                // Variation
  dL += 0.214 * Math.sin(2 * Mrad);                // Etc.
  dL -= 0.186 * Math.sin(Msrad);                   // Annual equation
  dL -= 0.114 * Math.sin(2 * Frad);

  // Add corrections
  L += dL;

  // Normalize to 0-360
  L = ((L % 360) + 360) % 360;

  return L;
};

/**
 * Calculate all planetary longitudes with proper epoch reference
 * @param {number} jd - Julian Day
 * @returns {Object} Object with planet longitudes
 */
export const calculatePlanetLongitudes = (jd) => {
  const d = jd - 2451545.0; // Days from J2000.0

  // Sun longitude (tropical)
  const sunM = (357.529 + 0.98560028 * d) * Math.PI / 180;
  const sunL = 280.459 + 0.98564736 * d + 1.915 * Math.sin(sunM) + 0.020 * Math.sin(2 * sunM);

  // Moon longitude
  const moonL = calculateMoonLongitude(jd);

  // Mars
  const marsM = (19.373 + 0.52402068 * d) * Math.PI / 180;
  const marsL = 355.45 + 0.5240711 * d + 10.691 * Math.sin(marsM) + 0.623 * Math.sin(2 * marsM);

  // Mercury
  const mercM = (174.795 + 4.09233445 * d) * Math.PI / 180;
  const mercL = 252.251 + 4.0932377 * d + 23.44 * Math.sin(mercM) + 2.9 * Math.sin(2 * mercM);

  // Jupiter
  const jupM = (20.020 + 0.08308529 * d) * Math.PI / 180;
  const jupL = 34.40 + 0.083091 * d + 5.555 * Math.sin(jupM) + 0.168 * Math.sin(2 * jupM);

  // Venus
  const venM = (50.416 + 1.60213034 * d) * Math.PI / 180;
  const venL = 181.979 + 1.6021687 * d + 0.761 * Math.sin(venM);

  // Saturn
  const satM = (317.021 + 0.03344414 * d) * Math.PI / 180;
  const satL = 50.077 + 0.033459 * d + 6.406 * Math.sin(satM) + 0.318 * Math.sin(2 * satM);

  // Rahu (Mean North Node) - moves retrograde
  const rahuL = 125.044 - 0.0529539 * d;

  // Ketu (opposite of Rahu)
  const ketuL = rahuL + 180;

  // Normalize all longitudes to 0-360
  const normalize = (l) => ((l % 360) + 360) % 360;

  return {
    Sun: normalize(sunL),
    Moon: normalize(moonL),
    Mars: normalize(marsL),
    Mercury: normalize(mercL),
    Jupiter: normalize(jupL),
    Venus: normalize(venL),
    Saturn: normalize(satL),
    Rahu: normalize(rahuL),
    Ketu: normalize(ketuL),
  };
};

/**
 * Calculate planetary positions based on birth date
 * @param {string} dob - Date of birth
 * @param {string} timeOfBirth - Time of birth
 * @param {number} timezone - Timezone offset
 * @returns {Array} Array of planet positions
 */
export const calculatePlanetPositions = (dob, timeOfBirth, timezone = 5.5) => {
  const date = new Date(dob);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Parse time of birth (default to 6:00 AM if not provided)
  let hour = 6, minute = 0;
  if (timeOfBirth) {
    [hour, minute] = timeOfBirth.split(':').map(Number);
  }

  // Calculate Julian Day for the birth moment
  const jd = getJulianDay(year, month, day, hour, minute, timezone);

  // Get Lahiri Ayanamsa for sidereal conversion
  const ayanamsa = getLahiriAyanamsa(jd);

  // Get tropical longitudes
  const tropicalLongs = calculatePlanetLongitudes(jd);

  // Nakshatras
  const nakshatras = NAKSHATRA_DATA.map(n => n.name);

  const sunTropical = tropicalLongs.Sun;

  const positions = PLANETS.map((planet) => {
    // Get tropical longitude and convert to sidereal
    const tropicalLong = tropicalLongs[planet.name];
    let siderealLong = tropicalLong - ayanamsa;
    siderealLong = ((siderealLong % 360) + 360) % 360;

    const signIndex = Math.floor(siderealLong / 30);
    const degreeInSign = siderealLong % 30;
    const degree = Math.floor(degreeInSign);
    const minutes = Math.floor((degreeInSign % 1) * 60);
    const seconds = Math.floor(((degreeInSign % 1) * 60 % 1) * 60);
    const house = signIndex + 1;

    // Nakshatra calculation (each nakshatra = 13°20' = 13.3333°)
    const nakshatraSpan = getNakshatraSpan();
    const nakshatraIndex = Math.floor(siderealLong / nakshatraSpan);
    const positionInNakshatra = siderealLong % nakshatraSpan;
    const pada = Math.floor(positionInNakshatra / (nakshatraSpan / 4)) + 1;

    // Retrograde calculation (simplified but more accurate)
    let retrograde = false;
    if (['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'].includes(planet.name)) {
      // Compare with Sun's elongation for inner planets
      const elongation = Math.abs(tropicalLong - sunTropical);
      if (planet.name === 'Mercury' && elongation > 18 && elongation < 28) retrograde = true;
      if (planet.name === 'Venus' && elongation > 29 && elongation < 47) retrograde = true;
      // Outer planets retrograde when near opposition
      if (['Mars', 'Jupiter', 'Saturn'].includes(planet.name)) {
        const oppAngle = Math.abs(180 - Math.abs(tropicalLong - sunTropical));
        if (oppAngle < 30) retrograde = true;
      }
    }

    return {
      ...planet,
      longitude: siderealLong,
      tropicalLongitude: tropicalLong,
      sign: SIGNS[signIndex],
      degree,
      minutes,
      seconds,
      house,
      nakshatra: nakshatras[nakshatraIndex % 27],
      nakshatraIndex: nakshatraIndex % 27,
      pada,
      retrograde,
      combust: planet.name !== 'Sun' && Math.abs(tropicalLong - sunTropical) < 10,
      ayanamsa,
    };
  });

  return positions;
};

/**
 * Calculate Janma Nakshatra and Rashi based on Moon position
 * @param {number} moonLongitude - Moon's sidereal longitude
 * @returns {Object} Janma details
 */
export const calculateJanmaNakshatraRashi = (moonLongitude) => {
  const nakshatraSpan = getNakshatraSpan();
  const padaSpan = getPadaSpan();

  const nakshatraIndex = Math.floor(moonLongitude / nakshatraSpan);
  const nakshatraData = NAKSHATRA_DATA[nakshatraIndex % 27];

  // Calculate pada (1-4)
  const positionInNakshatra = moonLongitude % nakshatraSpan;
  const pada = Math.floor(positionInNakshatra / padaSpan) + 1;

  // Calculate Rashi from Moon longitude (each rashi = 30°)
  const rashiIndex = Math.floor(moonLongitude / 30);
  const rashiData = RASHI_DATA[rashiIndex % 12];

  // Get naming syllable based on pada
  const namingSyllable = nakshatraData.syllables[pada - 1] || nakshatraData.syllables[0];

  return {
    nakshatra: nakshatraData,
    nakshatraIndex: nakshatraIndex % 27,
    pada,
    rashi: rashiData,
    rashiIndex: rashiIndex % 12,
    namingSyllable,
    moonDegree: moonLongitude,
    degreeInRashi: moonLongitude % 30,
  };
};

/**
 * Generate chart data from planetary positions
 * @param {Array} planets - Array of planet positions
 * @param {string} ascendantSign - Ascendant sign
 * @returns {Object} Chart data by house
 */
export const generateChartData = (planets, ascendantSign) => {
  const ascIndex = SIGNS.indexOf(ascendantSign);
  const chartData = {};

  // Initialize all houses
  for (let i = 1; i <= 12; i++) {
    chartData[i] = [];
  }

  // Place planets in houses based on their signs relative to ascendant
  planets.forEach((planet) => {
    const planetSignIndex = SIGNS.indexOf(planet.sign);
    let house = ((planetSignIndex - ascIndex + 12) % 12) + 1;

    chartData[house].push({
      name: planet.name,
      abbr: planet.abbr,
      degree: planet.degree,
      retrograde: planet.retrograde,
    });
  });

  return chartData;
};

/**
 * Calculate true Lagna (Ascendant) using Local Sidereal Time
 * @param {number} year - Year
 * @param {number} month - Month
 * @param {number} day - Day
 * @param {number} hour - Hour
 * @param {number} minute - Minute
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {number} timezone - Timezone offset
 * @returns {Object} Lagna data
 */
export const calculateLagna = (year, month, day, hour, minute, latitude, longitude, timezone) => {
  // Step 1: Julian Day
  const jd = getJulianDay(year, month, day, hour, minute, timezone);

  // Step 2: Centuries from J2000.0
  const T = (jd - 2451545.0) / 36525;

  // Step 3: Greenwich Mean Sidereal Time (GMST) in degrees
  let GMST = 280.46061837
    + 360.98564736629 * (jd - 2451545.0)
    + 0.000387933 * T * T
    - (T * T * T) / 38710000;
  GMST = ((GMST % 360) + 360) % 360;

  // Step 4: Local Sidereal Time = GMST + east longitude
  let LST = GMST + longitude;
  LST = ((LST % 360) + 360) % 360;

  // Step 5: Mean obliquity of the ecliptic
  const obliquity = 23.439291 - 0.0130042 * T - 1.64e-7 * T * T + 5.04e-7 * T * T * T;

  // Convert to radians
  const LST_rad = LST * Math.PI / 180;
  const lat_rad = latitude * Math.PI / 180;
  const obl_rad = obliquity * Math.PI / 180;

  // Step 6: Tropical Ascendant longitude
  let ascLong = Math.atan2(
    Math.cos(LST_rad),
    -(Math.sin(LST_rad) * Math.cos(obl_rad) + Math.tan(lat_rad) * Math.sin(obl_rad))
  );
  ascLong = ascLong * 180 / Math.PI;
  ascLong = ((ascLong % 360) + 360) % 360;

  // Step 7: Apply Lahiri Ayanamsa for sidereal Ascendant
  const ayanamsa = getLahiriAyanamsa(jd);
  let siderealAsc = ascLong - ayanamsa;
  siderealAsc = ((siderealAsc % 360) + 360) % 360;

  // Step 8: Determine Lagna sign and degree
  const signIndex = Math.floor(siderealAsc / 30) % 12;
  const degreeInSign = siderealAsc % 30;

  return {
    sign: SIGNS[signIndex],
    signIndex,
    degree: degreeInSign,
    tropicalLongitude: ascLong,
    siderealLongitude: siderealAsc,
    lst: LST,
    gmst: GMST,
    ayanamsa,
  };
};

/**
 * Calculate Manglik Dosha level with proper cancellation rules (Manglik Dosha Bhanga)
 * @param {Array} planets - Array of planet positions
 * @returns {Object} Manglik dosha details
 */
export const calculateManglikDosha = (planets, lagnaSign = 0) => {
  const mars = planets.find(p => p.name === 'Mars');
  if (!mars) return { isManglik: false, level: 'None', severity: 0 };

  // Calculate house relative to ascendant (whole sign houses)
  const marsSignIndex = mars.signIndex ?? SIGNS.indexOf(mars.sign);
  const marsHouse = ((marsSignIndex - lagnaSign + 12) % 12) + 1;
  const marsSign = mars.sign;

  if (!MANGLIK_HOUSES.includes(marsHouse)) {
    return { isManglik: false, level: 'None', severity: 0 };
  }

  // Calculate base severity
  let severity = 1;
  let level = 'Low';

  if ([1, 7, 8].includes(marsHouse)) {
    severity = 3;
    level = 'High';
  } else if ([4, 12].includes(marsHouse)) {
    severity = 2;
    level = 'Medium';
  }

  const cancelled = { isManglik: false, level: 'Cancelled', severity: 0, house: marsHouse };

  // ======== Cancellation Rules (Manglik Dosha Bhanga) ========

  // 1. Mars in own sign (Aries, Scorpio) — strong Mars doesn't cause dosha
  if (OWN_SIGNS['Mars']?.includes(marsSign)) return cancelled;

  // 2. Mars exalted (Capricorn) — exalted Mars doesn't cause dosha
  if (EXALTATION_SIGNS['Mars'] === marsSign) return cancelled;

  // 3. Sign-house specific cancellations (classical texts)
  if (
    (marsHouse === 1 && ['Aries', 'Leo', 'Sagittarius', 'Aquarius'].includes(marsSign)) ||
    (marsHouse === 2 && ['Gemini', 'Virgo'].includes(marsSign)) ||
    (marsHouse === 4 && ['Aries', 'Scorpio'].includes(marsSign)) ||
    (marsHouse === 7 && ['Cancer', 'Capricorn'].includes(marsSign)) ||
    (marsHouse === 8 && ['Sagittarius', 'Pisces'].includes(marsSign)) ||
    (marsHouse === 12 && ['Taurus', 'Libra'].includes(marsSign))
  ) return cancelled;

  // Helper to get house relative to ascendant
  const getHouse = (planet) => {
    if (!planet) return -1;
    const si = planet.signIndex ?? SIGNS.indexOf(planet.sign);
    if (si === -1 || si === undefined) return -1;
    return ((si - lagnaSign + 12) % 12) + 1;
  };

  // 4. Jupiter conjuncts Mars (same house) — benefic conjunction cancels
  const jupiter = planets.find(p => p.name === 'Jupiter');
  const jupiterHouse = getHouse(jupiter);
  if (jupiter && jupiterHouse === marsHouse) return cancelled;

  // 5. Jupiter aspects Mars — Jupiter's special aspects: 5th, 7th, 9th from Jupiter
  if (jupiter) {
    const jupiterAspects = [
      ((jupiterHouse + 4 - 1) % 12) + 1, // 5th aspect
      ((jupiterHouse + 6 - 1) % 12) + 1, // 7th aspect
      ((jupiterHouse + 8 - 1) % 12) + 1, // 9th aspect
    ];
    if (jupiterAspects.includes(marsHouse)) return cancelled;
  }

  // 6. Venus conjuncts Mars in 7th house
  const venus = planets.find(p => p.name === 'Venus');
  const venusHouse = getHouse(venus);
  if (venus && venusHouse === marsHouse && marsHouse === 7) return cancelled;

  // 7. Moon conjuncts Mars — reduces severity
  const moon = planets.find(p => p.name === 'Moon');
  const moonHouse = getHouse(moon);
  if (moon && moonHouse === marsHouse) {
    severity = Math.max(1, severity - 2);
    level = 'Low';
  }

  return { isManglik: true, level, severity, house: marsHouse };
};

/**
 * Calculate planetary strength and dignity
 * @param {Object} planet - Planet data
 * @param {Array} allPlanets - All planet positions
 * @returns {Object} Strength data
 */
export const calculatePlanetaryStrength = (planet, allPlanets) => {
  let strength = 50; // Base strength
  let dignityStatus = 'Neutral';

  // Check dignity
  if (EXALTATION_SIGNS[planet.name] === planet.sign) {
    strength += 30;
    dignityStatus = 'Exalted';
  } else if (DEBILITATION_SIGNS[planet.name] === planet.sign) {
    strength -= 30;
    dignityStatus = 'Debilitated';
  } else if (OWN_SIGNS[planet.name]?.includes(planet.sign)) {
    strength += 20;
    dignityStatus = 'Own Sign';
  }

  // Retrograde bonus for outer planets
  if (planet.retrograde && ['Mars', 'Jupiter', 'Saturn'].includes(planet.name)) {
    strength += 10;
  }

  // Combust penalty
  if (planet.combust) {
    strength -= 20;
    if (dignityStatus === 'Neutral') dignityStatus = 'Combust';
  }

  // Benefic house placement
  if (BENEFIC_HOUSES.includes(planet.house)) {
    strength += 10;
  }

  // Cap the strength
  strength = Math.max(0, Math.min(100, strength));

  let level = 'Medium';
  if (strength >= 70) level = 'Strong';
  else if (strength <= 30) level = 'Weak';

  return { strength, level, dignityStatus };
};

/**
 * Calculate current planetary period (Vara)
 * @returns {Object} Current planetary period data
 */
export const getCurrentPlanetaryPeriod = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday

  // Current day lord
  const currentDayLord = DAY_LORDS[dayOfWeek];
  const currentDayLordHi = DAY_LORDS_HI[dayOfWeek];

  // Calculate current hora (each hora = 1 hour, starting from sunrise)
  const hour = today.getHours();
  const sunriseHour = 6; // Approximate sunrise
  const hoursFromSunrise = (hour - sunriseHour + 24) % 24;
  const horaIndex = (dayOfWeek + hoursFromSunrise) % 7;
  const horaLord = DAY_LORDS[horaIndex];

  // Weekly planetary schedule
  const weeklyPeriods = DAY_LORDS.map((lord, idx) => ({
    day: DAY_NAMES[idx],
    dayHi: DAY_NAMES_HI[idx],
    lord,
    lordHi: DAY_LORDS_HI[idx],
    isToday: idx === dayOfWeek
  }));

  return {
    currentDayLord,
    currentDayLordHi,
    horaLord,
    weeklyPeriods,
    dayOfWeek
  };
};

/**
 * Calculate house strength and remedies
 * @param {Array} planets - Array of planet positions
 * @param {string} ascendantSign - Ascendant sign
 * @returns {Array} House analysis data
 */
export const calculateHouseAnalysis = (planets, ascendantSign) => {
  const ascIndex = SIGNS.indexOf(ascendantSign);
  const houses = [];

  for (let i = 1; i <= 12; i++) {
    const houseSign = SIGNS[(ascIndex + i - 1) % 12];
    const lord = HOUSE_LORDS[houseSign];
    const lordPlanet = planets.find(p => p.name === lord);

    // Calculate house strength
    let strength = 50;
    const planetsInHouse = planets.filter(p => {
      const si = SIGNS.indexOf(p.sign);
      const h = ((si - ascIndex + 12) % 12) + 1;
      return h === i;
    });

    // Benefics strengthen, malefics can challenge
    planetsInHouse.forEach(p => {
      if (BENEFIC_PLANETS.includes(p.name)) strength += 15;
      else if (MALEFIC_PLANETS.includes(p.name)) strength -= 10;
    });

    // Lord's condition affects house
    if (lordPlanet) {
      const lordStrength = calculatePlanetaryStrength(lordPlanet, planets);
      strength += (lordStrength.strength - 50) * 0.3;
    }

    strength = Math.max(0, Math.min(100, strength));
    let status = strength >= 60 ? 'Strong' : strength <= 40 ? 'Weak' : 'Moderate';

    houses.push({
      number: i,
      sign: houseSign,
      lord,
      significance: HOUSE_SIGNIFICANCE[i],
      strength,
      status,
      planets: planetsInHouse.map(p => p.name),
      remedy: status === 'Weak' ? PLANET_REMEDIES[lord] : null
    });
  }

  return houses;
};

/**
 * Calculate Panchang (Hindu Almanac) for a given birth date/time
 * Returns Tithi, Nakshatra, Yoga, Karana, and Vara
 * @param {string} dob - Date of birth (YYYY-MM-DD)
 * @param {string} timeOfBirth - Time of birth (HH:MM)
 * @param {number} timezone - UTC offset
 * @returns {Object} Panchang data
 */
export const calculatePanchang = (dob, timeOfBirth, timezone = 5.5) => {
  const date = new Date(dob);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  let hour = 6, minute = 0;
  if (timeOfBirth) {
    [hour, minute] = timeOfBirth.split(':').map(Number);
  }

  const jd = getJulianDay(year, month, day, hour, minute, timezone);
  const ayanamsa = getLahiriAyanamsa(jd);

  // Get tropical longitudes and convert to sidereal
  const tropLongs = calculatePlanetLongitudes(jd);
  const sunLong = ((tropLongs.Sun - ayanamsa) % 360 + 360) % 360;
  const moonLong = ((tropLongs.Moon - ayanamsa) % 360 + 360) % 360;

  // --- TITHI ---
  // Tithi = elongation of Moon from Sun in steps of 12°
  let diff = moonLong - sunLong;
  if (diff < 0) diff += 360;
  const tithiNumber = Math.floor(diff / 12) + 1; // 1–30

  const TITHI_NAMES = [
    'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
    'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
    'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima',
    'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
    'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
    'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Amavasya'
  ];
  const TITHI_NAMES_TE = [
    'పాడ్యమి', 'విదియ', 'తదియ', 'చవితి', 'పంచమి',
    'షష్ఠి', 'సప్తమి', 'అష్టమి', 'నవమి', 'దశమి',
    'ఏకాదశి', 'ద్వాదశి', 'త్రయోదశి', 'చతుర్దశి', 'పౌర్ణమి',
    'పాడ్యమి', 'విదియ', 'తదియ', 'చవితి', 'పంచమి',
    'షష్ఠి', 'సప్తమి', 'అష్టమి', 'నవమి', 'దశమి',
    'ఏకాదశి', 'ద్వాదశి', 'త్రయోదశి', 'చతుర్దశి', 'అమావాస్య'
  ];

  const tithiIndex = (tithiNumber - 1) % 30;
  const paksha = tithiNumber <= 15 ? 'Shukla' : 'Krishna';
  const pakshaTe = tithiNumber <= 15 ? 'శుక్ల పక్షం' : 'కృష్ణ పక్షం';

  // --- YOGA ---
  // Yoga = (Sun longitude + Moon longitude) / (360/27)
  let sumLong = sunLong + moonLong;
  if (sumLong >= 360) sumLong -= 360;
  const yogaNumber = Math.floor(sumLong / (360 / 27)) + 1;

  const YOGA_NAMES = [
    'Vishkumbha', 'Priti', 'Ayushman', 'Saubhagya', 'Shobhana', 'Atiganda',
    'Sukarma', 'Dhriti', 'Shoola', 'Ganda', 'Vriddhi', 'Dhruva', 'Vyaghata',
    'Harshana', 'Vajra', 'Siddhi', 'Vyatipata', 'Variyan', 'Parigha', 'Shiva',
    'Siddha', 'Sadhya', 'Shubha', 'Shukla', 'Brahma', 'Indra', 'Vaidhriti'
  ];
  const YOGA_NAMES_TE = [
    'విష్కంభ', 'ప్రీతి', 'ఆయుష్మాన్', 'సౌభాగ్య', 'శోభన', 'అతిగండ',
    'సుకర్మ', 'ధృతి', 'శూల', 'గండ', 'వృద్ధి', 'ధ్రువ', 'వ్యాఘాత',
    'హర్షణ', 'వజ్ర', 'సిద్ధి', 'వ్యతీపాత', 'వరీయాన్', 'పరిఘ', 'శివ',
    'సిద్ధ', 'సాధ్య', 'శుభ', 'శుక్ల', 'బ్రహ్మ', 'ఇంద్ర', 'వైధృతి'
  ];

  const yogaIndex = (yogaNumber - 1) % 27;

  // --- KARANA ---
  // Each Tithi has 2 Karanas (half-tithi = 6° of elongation)
  // There are 11 Karanas: 4 fixed + 7 rotating
  const KARANA_NAMES = [
    'Bava', 'Balava', 'Kaulava', 'Taitila', 'Garaja', 'Vanija', 'Vishti'
  ];
  const KARANA_NAMES_TE = [
    'బవ', 'బాలవ', 'కౌలవ', 'తైతుల', 'గరజ', 'వణిజ', 'భద్ర (విష్టి)'
  ];
  const FIXED_KARANA = [
    { name: 'Kimstughna', nameTe: 'కింస్తుఘ్న' },
    { name: 'Shakuni', nameTe: 'శకుని' },
    { name: 'Chatushpada', nameTe: 'చతుష్పాద' },
    { name: 'Nagava', nameTe: 'నాగవ' },
  ];

  // Karana number (1-60 in a lunar month, 2 per tithi)
  const karanaInTithi = Math.floor((diff % 12) / 6); // 0 or 1 => first or second half
  const karanaAbsolute = (tithiIndex * 2) + karanaInTithi;

  let karanaName, karanaTe;
  if (karanaAbsolute === 0) {
    // First karana of Shukla Pratipada
    karanaName = FIXED_KARANA[0].name;
    karanaTe = FIXED_KARANA[0].nameTe;
  } else if (karanaAbsolute >= 57) {
    // Last 3 karanas of Amavasya
    const fixedIdx = karanaAbsolute - 57 + 1;
    karanaName = FIXED_KARANA[Math.min(fixedIdx, 3)].name;
    karanaTe = FIXED_KARANA[Math.min(fixedIdx, 3)].nameTe;
  } else {
    const rotIdx = (karanaAbsolute - 1) % 7;
    karanaName = KARANA_NAMES[rotIdx];
    karanaTe = KARANA_NAMES_TE[rotIdx];
  }

  // --- VARA (Weekday) ---
  const VARA_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const VARA_VEDIC = ['Ravivara', 'Somavara', 'Mangalavara', 'Budhavara', 'Guruvara', 'Shukravara', 'Shanivara'];
  const VARA_TE = ['ఆదివారం', 'సోమవారం', 'మంగళవారం', 'బుధవారం', 'గురువారం', 'శుక్రవారం', 'శనివారం'];

  const varaIndex = Math.floor(jd + 1.5) % 7;

  // --- NAKSHATRA (from Moon) ---
  const nakshatraSpan = 360 / 27;
  const nakshatraIndex = Math.floor(moonLong / nakshatraSpan) % 27;
  const posInNakshatra = moonLong % nakshatraSpan;
  const pada = Math.floor(posInNakshatra / (nakshatraSpan / 4)) + 1;
  const nk = NAKSHATRA_DATA[nakshatraIndex];

  return {
    tithi: {
      number: (tithiIndex % 15) + 1,
      name: TITHI_NAMES[tithiIndex],
      nameTe: TITHI_NAMES_TE[tithiIndex],
      paksha,
      pakshaTe,
    },
    nakshatra: {
      name: nk.name,
      nameTe: nk.nameTe,
      pada,
      lord: nk.lord,
    },
    yoga: {
      number: yogaNumber,
      name: YOGA_NAMES[yogaIndex],
      nameTe: YOGA_NAMES_TE[yogaIndex],
    },
    karana: {
      name: karanaName,
      nameTe: karanaTe,
    },
    vara: {
      name: VARA_NAMES[varaIndex],
      vedic: VARA_VEDIC[varaIndex],
      nameTe: VARA_TE[varaIndex],
    },
  };
};

/**
 * Calculate Rahu Kalam, Yamagandam, and Gulika Kalam for a given day
 * These are inauspicious periods based on weekday and sunrise/sunset
 * @param {string} dob - Date of birth (YYYY-MM-DD)
 * @param {number} latitude - Latitude of birthplace
 * @returns {Object} Kalam timings
 */
export const calculateKalamTimings = (dob, latitude = 13.0827) => {
  const date = new Date(dob);
  const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ...

  // Approximate sunrise/sunset (simplified for demo - ideally use proper calculation)
  // Using standard 6 AM sunrise and 6 PM sunset, adjusted slightly for latitude
  const latAdjust = Math.abs(latitude - 23.5) / 23.5 * 0.5; // rough adjustment
  const sunriseHour = 6 - latAdjust;
  const sunsetHour = 18 + latAdjust;
  const dayDuration = sunsetHour - sunriseHour; // ~12 hours
  const periodLength = dayDuration / 8; // Each period is 1/8 of day length

  // Rahu Kalam period number for each day (1-indexed periods from sunrise)
  // Sunday=8, Monday=2, Tuesday=7, Wednesday=5, Thursday=6, Friday=4, Saturday=3
  const RAHU_PERIOD = [8, 2, 7, 5, 6, 4, 3];

  // Yamagandam period number
  // Sunday=5, Monday=4, Tuesday=3, Wednesday=2, Thursday=1, Friday=7, Saturday=6
  const YAMA_PERIOD = [5, 4, 3, 2, 1, 7, 6];

  // Gulika Kalam period number
  // Sunday=7, Monday=6, Tuesday=5, Wednesday=4, Thursday=3, Friday=2, Saturday=1
  const GULIKA_PERIOD = [7, 6, 5, 4, 3, 2, 1];

  const formatTime = (decimalHours) => {
    const h = Math.floor(decimalHours);
    const m = Math.round((decimalHours - h) * 60);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const getKalamTime = (periodNum) => {
    const startHour = sunriseHour + (periodNum - 1) * periodLength;
    const endHour = startHour + periodLength;
    return {
      start: formatTime(startHour),
      end: formatTime(endHour),
      startDecimal: startHour,
      endDecimal: endHour,
    };
  };

  const rahuKalam = getKalamTime(RAHU_PERIOD[dayOfWeek]);
  const yamagandam = getKalamTime(YAMA_PERIOD[dayOfWeek]);
  const gulikaKalam = getKalamTime(GULIKA_PERIOD[dayOfWeek]);

  // Check if birth time falls in any inauspicious period
  const DAY_NAMES_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DAY_NAMES_TE_LOCAL = ['ఆదివారం', 'సోమవారం', 'మంగళవారం', 'బుధవారం', 'గురువారం', 'శుక్రవారం', 'శనివారం'];

  return {
    dayOfWeek: DAY_NAMES_EN[dayOfWeek],
    dayOfWeekTe: DAY_NAMES_TE_LOCAL[dayOfWeek],
    sunrise: formatTime(sunriseHour),
    sunset: formatTime(sunsetHour),
    rahuKalam: {
      ...rahuKalam,
      name: 'Rahu Kalam',
      nameTe: 'రాహు కాలం',
      description: 'Inauspicious for starting new ventures',
      descriptionTe: 'కొత్త పనులు ప్రారంభించడానికి శుభం కాదు',
    },
    yamagandam: {
      ...yamagandam,
      name: 'Yamagandam',
      nameTe: 'యమగండం',
      description: 'Inauspicious for travel and important tasks',
      descriptionTe: 'ప్రయాణం, ముఖ్యమైన పనులకు శుభం కాదు',
    },
    gulikaKalam: {
      ...gulikaKalam,
      name: 'Gulika Kalam',
      nameTe: 'గుళిక కాలం',
      description: 'Inauspicious for health-related activities',
      descriptionTe: 'ఆరోగ్య సంబంధిత పనులకు శుభం కాదు',
    },
  };
};

/**
 * Calculate lucky information based on birth Nakshatra and Rashi
 * @param {Object} janmaDetails - Janma Nakshatra and Rashi details
 * @param {number} lagnaSign - Ascendant sign index (0-11)
 * @returns {Object} Lucky information
 */
export const calculateLuckyInfo = (janmaDetails, lagnaSign = 0) => {
  if (!janmaDetails?.nakshatra || !janmaDetails?.rashi) return null;

  const nakshatraLord = janmaDetails.nakshatra.lord;
  const rashiLord = janmaDetails.rashi.lord;

  // Lucky numbers based on nakshatra lord (traditional numerology)
  const PLANET_NUMBERS = {
    'Sun': [1, 10, 19, 28],
    'Moon': [2, 11, 20, 29],
    'Mars': [9, 18, 27],
    'Mercury': [5, 14, 23],
    'Jupiter': [3, 12, 21, 30],
    'Venus': [6, 15, 24],
    'Saturn': [8, 17, 26],
    'Rahu': [4, 13, 22, 31],
    'Ketu': [7, 16, 25],
  };

  // Lucky colors based on ruling planet
  const PLANET_COLORS = {
    'Sun': { en: 'Orange, Gold, Red', te: 'నారింజ, బంగారం, ఎరుపు' },
    'Moon': { en: 'White, Silver, Cream', te: 'తెలుపు, వెండి, క్రీం' },
    'Mars': { en: 'Red, Coral, Scarlet', te: 'ఎరుపు, పగడం, స్కార్లెట్' },
    'Mercury': { en: 'Green, Emerald', te: 'ఆకుపచ్చ, పచ్చ' },
    'Jupiter': { en: 'Yellow, Gold, Orange', te: 'పసుపు, బంగారం, నారింజ' },
    'Venus': { en: 'White, Pink, Light Blue', te: 'తెలుపు, గులాబీ, లేత నీలం' },
    'Saturn': { en: 'Blue, Black, Purple', te: 'నీలం, నలుపు, ఊదా' },
    'Rahu': { en: 'Smoky Grey, Ultramarine', te: 'బూడిద, అల్ట్రామెరైన్' },
    'Ketu': { en: 'Grey, Brown', te: 'బూడిద, గోధుమ' },
  };

  // Lucky days based on ruling planet
  const PLANET_DAYS = {
    'Sun': { en: 'Sunday', te: 'ఆదివారం' },
    'Moon': { en: 'Monday', te: 'సోమవారం' },
    'Mars': { en: 'Tuesday', te: 'మంగళవారం' },
    'Mercury': { en: 'Wednesday', te: 'బుధవారం' },
    'Jupiter': { en: 'Thursday', te: 'గురువారం' },
    'Venus': { en: 'Friday', te: 'శుక్రవారం' },
    'Saturn': { en: 'Saturday', te: 'శనివారం' },
    'Rahu': { en: 'Saturday', te: 'శనివారం' },
    'Ketu': { en: 'Tuesday', te: 'మంగళవారం' },
  };

  // Gemstones based on Rashi lord (Moon sign)
  const PLANET_GEMS = {
    'Sun': { name: 'Ruby', nameTe: 'కెంపు', finger: 'Ring finger' },
    'Moon': { name: 'Pearl', nameTe: 'ముత్యం', finger: 'Little finger' },
    'Mars': { name: 'Red Coral', nameTe: 'పగడం', finger: 'Ring finger' },
    'Mercury': { name: 'Emerald', nameTe: 'పచ్చ', finger: 'Little finger' },
    'Jupiter': { name: 'Yellow Sapphire', nameTe: 'పుష్యరాగం', finger: 'Index finger' },
    'Venus': { name: 'Diamond', nameTe: 'వజ్రం', finger: 'Middle finger' },
    'Saturn': { name: 'Blue Sapphire', nameTe: 'నీలం', finger: 'Middle finger' },
    'Rahu': { name: 'Hessonite', nameTe: 'గోమేదికం', finger: 'Middle finger' },
    'Ketu': { name: "Cat's Eye", nameTe: 'వైడూర్యం', finger: 'Little finger' },
  };

  // Nakshatra compatibility groups (Tara-based simplified)
  const NAKSHATRA_COMPAT = {
    'Ashwini': ['Bharani', 'Rohini', 'Mrigashira', 'Punarvasu', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Shravana', 'Revati'],
    'Bharani': ['Ashwini', 'Krittika', 'Rohini', 'Ardra', 'Punarvasu', 'Magha', 'Purva Phalguni', 'Chitra', 'Vishakha', 'Mula', 'Purva Ashada', 'Dhanishta', 'Shatabhisha'],
    'Krittika': ['Bharani', 'Rohini', 'Mrigashira', 'Pushya', 'Ashlesha', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Shravana', 'Purva Bhadrapada'],
    'Rohini': ['Ashwini', 'Krittika', 'Mrigashira', 'Ardra', 'Magha', 'Purva Phalguni', 'Chitra', 'Vishakha', 'Mula', 'Purva Ashada', 'Dhanishta', 'Uttara Bhadrapada'],
    'Mrigashira': ['Ashwini', 'Krittika', 'Rohini', 'Punarvasu', 'Pushya', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Shravana', 'Revati'],
    'Ardra': ['Bharani', 'Rohini', 'Punarvasu', 'Pushya', 'Magha', 'Purva Phalguni', 'Chitra', 'Vishakha', 'Mula', 'Purva Ashada', 'Dhanishta', 'Shatabhisha'],
    'Punarvasu': ['Ashwini', 'Mrigashira', 'Ardra', 'Pushya', 'Ashlesha', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Shravana', 'Purva Bhadrapada'],
    'Pushya': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Chitra', 'Vishakha', 'Mula', 'Purva Ashada', 'Uttara Bhadrapada'],
    'Ashlesha': ['Krittika', 'Punarvasu', 'Pushya', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Dhanishta', 'Revati'],
    'Magha': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Ashlesha', 'Purva Phalguni', 'Chitra', 'Vishakha', 'Mula', 'Purva Ashada', 'Dhanishta', 'Shatabhisha'],
    'Purva Phalguni': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Ashlesha', 'Magha', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Purva Bhadrapada'],
    'Uttara Phalguni': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Purva Phalguni', 'Hasta', 'Chitra', 'Vishakha', 'Mula', 'Shravana', 'Uttara Bhadrapada'],
    'Hasta': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Purva Phalguni', 'Uttara Phalguni', 'Swati', 'Anuradha', 'Uttarashada', 'Shravana', 'Revati'],
    'Chitra': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Magha', 'Uttara Phalguni', 'Swati', 'Vishakha', 'Mula', 'Purva Ashada', 'Dhanishta', 'Shatabhisha'],
    'Swati': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Hasta', 'Chitra', 'Vishakha', 'Anuradha', 'Uttarashada', 'Shravana', 'Purva Bhadrapada', 'Revati'],
    'Vishakha': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Magha', 'Uttara Phalguni', 'Chitra', 'Swati', 'Mula', 'Purva Ashada', 'Dhanishta', 'Uttara Bhadrapada'],
    'Anuradha': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Purva Phalguni', 'Hasta', 'Swati', 'Jyeshtha', 'Uttarashada', 'Shravana', 'Revati'],
    'Jyeshtha': ['Anuradha', 'Mula', 'Purva Ashada', 'Uttarashada', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati', 'Ashwini', 'Bharani'],
    'Mula': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Magha', 'Uttara Phalguni', 'Chitra', 'Vishakha', 'Jyeshtha', 'Purva Ashada', 'Dhanishta', 'Shatabhisha'],
    'Purva Ashada': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Magha', 'Chitra', 'Vishakha', 'Jyeshtha', 'Mula', 'Uttarashada', 'Dhanishta', 'Uttara Bhadrapada'],
    'Uttarashada': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Purva Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Purva Ashada', 'Shravana', 'Revati'],
    'Shravana': ['Ashwini', 'Krittika', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Purva Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Dhanishta', 'Purva Bhadrapada'],
    'Dhanishta': ['Bharani', 'Rohini', 'Ardra', 'Pushya', 'Ashlesha', 'Magha', 'Chitra', 'Vishakha', 'Jyeshtha', 'Mula', 'Shravana', 'Shatabhisha'],
    'Shatabhisha': ['Bharani', 'Ardra', 'Magha', 'Chitra', 'Jyeshtha', 'Mula', 'Dhanishta', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati', 'Ashwini', 'Krittika'],
    'Purva Bhadrapada': ['Krittika', 'Punarvasu', 'Purva Phalguni', 'Swati', 'Shravana', 'Shatabhisha', 'Uttara Bhadrapada', 'Revati', 'Ashwini', 'Bharani', 'Rohini', 'Mrigashira'],
    'Uttara Bhadrapada': ['Rohini', 'Pushya', 'Uttara Phalguni', 'Vishakha', 'Purva Ashada', 'Shatabhisha', 'Purva Bhadrapada', 'Revati', 'Ashwini', 'Bharani', 'Krittika', 'Ardra'],
    'Revati': ['Ashwini', 'Mrigashira', 'Punarvasu', 'Ashlesha', 'Hasta', 'Swati', 'Anuradha', 'Uttarashada', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Bharani'],
  };

  // Lagna-based lucky metal
  const LAGNA_METALS = [
    { en: 'Copper', te: 'రాగి' },      // Aries
    { en: 'Silver', te: 'వెండి' },     // Taurus
    { en: 'Bronze', te: 'కంచు' },      // Gemini
    { en: 'Silver', te: 'వెండి' },     // Cancer
    { en: 'Gold', te: 'బంగారం' },      // Leo
    { en: 'Bronze', te: 'కంచు' },      // Virgo
    { en: 'Platinum', te: 'ప్లాటినం' }, // Libra
    { en: 'Copper', te: 'రాగి' },      // Scorpio
    { en: 'Gold', te: 'బంగారం' },      // Sagittarius
    { en: 'Iron', te: 'ఇనుము' },       // Capricorn
    { en: 'Iron', te: 'ఇనుము' },       // Aquarius
    { en: 'Gold', te: 'బంగారం' },      // Pisces
  ];

  const luckyNumbers = PLANET_NUMBERS[nakshatraLord] || [1, 9];
  const luckyColor = PLANET_COLORS[rashiLord] || { en: 'White', te: 'తెలుపు' };
  const luckyDay = PLANET_DAYS[nakshatraLord] || { en: 'Sunday', te: 'ఆదివారం' };
  const gemstone = PLANET_GEMS[rashiLord] || { name: 'Pearl', nameTe: 'ముత్యం', finger: 'Little finger' };
  const compatNakshatras = NAKSHATRA_COMPAT[janmaDetails.nakshatra.name] || [];
  const luckyMetal = LAGNA_METALS[lagnaSign] || { en: 'Gold', te: 'బంగారం' };

  return {
    luckyNumbers: luckyNumbers.slice(0, 4),
    luckyColor,
    luckyDay,
    gemstone,
    luckyMetal,
    compatibleNakshatras: compatNakshatras.slice(0, 8), // Show top 8 compatible stars
    nakshatraLord,
    rashiLord,
  };
};

/**
 * Calculate basic life predictions based on planetary positions
 * @param {Array} planetPositions - Array of planet position objects
 * @param {number} lagnaSign - Ascendant sign index (0-11)
 * @param {Object} janmaDetails - Nakshatra/Rashi details
 * @returns {Object} Life predictions
 */
export const calculateLifePredictions = (planetPositions, lagnaSign, janmaDetails) => {
  if (!planetPositions || planetPositions.length === 0) return null;

  const getPlanet = (name) => planetPositions.find(p => p.name === name) || {};

  const sun = getPlanet('Sun');
  const moon = getPlanet('Moon');
  const mars = getPlanet('Mars');
  const mercury = getPlanet('Mercury');
  const jupiter = getPlanet('Jupiter');
  const venus = getPlanet('Venus');
  const saturn = getPlanet('Saturn');

  // Helper to get house from lagna
  const getHouse = (planet) => {
    if (!planet.signIndex && planet.signIndex !== 0) return -1;
    return ((planet.signIndex - lagnaSign + 12) % 12) + 1;
  };

  const jupiterHouse = getHouse(jupiter);
  const venusHouse = getHouse(venus);
  const saturnHouse = getHouse(saturn);
  const marsHouse = getHouse(mars);
  const sunHouse = getHouse(sun);
  const mercuryHouse = getHouse(mercury);
  const moonHouse = getHouse(moon);

  // === MARRIAGE TIMING ===
  let marriageTiming = '';
  let marriageTimingTe = '';

  // 7th house analysis for marriage
  const seventhLord = HOUSE_LORDS[(lagnaSign + 6) % 12];

  if (jupiterHouse === 7 || jupiterHouse === 1 || jupiterHouse === 5) {
    marriageTiming = 'Early to mid-20s likely. Jupiter\'s blessing indicates a harmonious marriage.';
    marriageTimingTe = 'సాధారణంగా 20ల ప్రారంభంలో వివాహం. బృహస్పతి ఆశీర్వాదం అనుకూల వివాహాన్ని సూచిస్తుంది.';
  } else if (venusHouse === 7 || venusHouse === 2) {
    marriageTiming = 'Mid-20s expected. Venus placement suggests a loving, romantic partner.';
    marriageTimingTe = '20ల మధ్యలో వివాహం అంచనా. శుక్రుని స్థానం ప్రేమగల భాగస్వామిని సూచిస్తుంది.';
  } else if (saturnHouse === 7) {
    marriageTiming = 'Late marriage likely (after 28-30). Saturn brings maturity and a serious partner.';
    marriageTimingTe = 'ఆలస్యంగా వివాహం (28-30 తర్వాత). శని పరిణతి, సీరియస్ భాగస్వామిని తెస్తుంది.';
  } else if (marsHouse === 7) {
    marriageTiming = 'Marriage around 25-28. Mars indicates a passionate but potentially challenging relationship.';
    marriageTimingTe = '25-28 చుట్టు వివాహం. కుజుడు ఆవేశపూరిత కానీ సవాళ్ళతో కూడిన సంబంధాన్ని సూచిస్తుంది.';
  } else {
    marriageTiming = 'Marriage timing depends on Dasha periods. Generally favorable between 24-30.';
    marriageTimingTe = 'వివాహ సమయం దశల మీద ఆధారపడి ఉంటుంది. సాధారణంగా 24-30 మధ్య అనుకూలం.';
  }

  // === CAREER OUTLOOK ===
  let careerOutlook = '';
  let careerOutlookTe = '';

  // 10th house analysis
  const tenthLord = HOUSE_LORDS[(lagnaSign + 9) % 12];

  if (sunHouse === 10 || sunHouse === 1) {
    careerOutlook = 'Strong leadership potential. Government, politics, administration, or management roles suit you.';
    careerOutlookTe = 'నాయకత్వ సామర్థ్యం బలంగా ఉంది. ప్రభుత్వ, రాజకీయ, పరిపాలన లేదా నిర్వహణ పాత్రలు అనుకూలం.';
  } else if (mercuryHouse === 10 || mercuryHouse === 1) {
    careerOutlook = 'Excellent for communication, writing, business, IT, finance, or teaching careers.';
    careerOutlookTe = 'కమ్యూనికేషన్, రచన, వ్యాపారం, IT, ఫైనాన్స్ లేదా బోధనకు అద్భుతం.';
  } else if (jupiterHouse === 10 || (jupiterHouse === 9 && sunHouse === 10)) {
    careerOutlook = 'Success in education, law, consulting, spirituality, or advisory roles.';
    careerOutlookTe = 'విద్య, న్యాయం, కన్సల్టింగ్, ఆధ్యాత్మికత లేదా సలహాదారు పాత్రలలో విజయం.';
  } else if (venusHouse === 10) {
    careerOutlook = 'Arts, entertainment, fashion, luxury goods, hospitality, or creative fields suit you.';
    careerOutlookTe = 'కళలు, వినోదం, ఫ్యాషన్, విలాసవంతమైన వస్తువులు, ఆతిథ్యం లేదా సృజనాత్మక రంగాలు అనుకూలం.';
  } else if (marsHouse === 10) {
    careerOutlook = 'Engineering, military, sports, surgery, real estate, or competitive fields suit you.';
    careerOutlookTe = 'ఇంజనీరింగ్, సైనిక, క్రీడలు, శస్త్రచికిత్స, రియల్ ఎస్టేట్ లేదా పోటీ రంగాలు అనుకూలం.';
  } else if (saturnHouse === 10) {
    careerOutlook = 'Slow but steady rise. Success in research, mining, agriculture, labor, or traditional industries.';
    careerOutlookTe = 'నెమ్మది కానీ స్థిరమైన పురోగతి. పరిశోధన, త్రవ్వకం, వ్యవసాయం, కార్మిక రంగాలలో విజయం.';
  } else {
    careerOutlook = `Career success through ${tenthLord}-related fields. Focus on skill development in your 20s.`;
    careerOutlookTe = `${tenthLord} సంబంధిత రంగాలలో వృత్తి విజయం. 20లలో నైపుణ్య అభివృద్ధిపై దృష్టి పెట్టండి.`;
  }

  // === HEALTH CONCERNS ===
  let healthConcerns = '';
  let healthConcernsTe = '';

  // 6th house and lagna analysis
  const sixthLord = HOUSE_LORDS[(lagnaSign + 5) % 12];

  // Check weak points based on planets
  const healthIssues = [];
  const healthIssuesTe = [];

  if (sunHouse === 6 || sunHouse === 8 || sunHouse === 12) {
    healthIssues.push('Eyes, heart, bones — protect from heat');
    healthIssuesTe.push('కళ్ళు, గుండె, ఎముకలు — వేడి నుండి రక్షించుకోండి');
  }
  if (moonHouse === 6 || moonHouse === 8 || moon.retrograde) {
    healthIssues.push('Mental stress, digestive issues — practice meditation');
    healthIssuesTe.push('మానసిక ఒత్తిడి, జీర్ణ సమస్యలు — ధ్యానం చేయండి');
  }
  if (marsHouse === 6 || marsHouse === 8 || mars.combust) {
    healthIssues.push('Accidents, blood pressure, inflammation — avoid rash decisions');
    healthIssuesTe.push('ప్రమాదాలు, రక్తపోటు, వాపు — తొందరపాటు నిర్ణయాలు మానండి');
  }
  if (saturnHouse === 1 || saturnHouse === 8) {
    healthIssues.push('Joint pains, chronic issues — regular exercise important');
    healthIssuesTe.push('కీళ్ళ నొప్పులు, దీర్ఘకాలిక సమస్యలు — క్రమం తప్పకుండా వ్యాయామం ముఖ్యం');
  }

  if (healthIssues.length === 0) {
    healthConcerns = 'Generally good health indicated. Maintain a balanced lifestyle for lasting wellness.';
    healthConcernsTe = 'సాధారణంగా మంచి ఆరోగ్యం సూచించబడింది. శాశ్వత ఆరోగ్యం కోసం సమతుల్య జీవనశైలిని కొనసాగించండి.';
  } else {
    healthConcerns = healthIssues.join('; ') + '. Regular checkups advised.';
    healthConcernsTe = healthIssuesTe.join('; ') + '. క్రమం తప్పకుండా చెకప్‌లు సలహా.';
  }

  // === GOOD/BAD PERIODS (Simplified transit-based) ===
  // Based on Saturn's 7.5 year cycles (Sade Sati periods)
  const moonSign = janmaDetails?.rashi?.name || 'Unknown';
  let sadeSatiStatus = '';
  let sadeSatiStatusTe = '';

  // Current Saturn position (approximate for 2024-2026 - Saturn in Pisces/Aquarius)
  const saturnCurrentSign = 'Aquarius'; // Approximate
  const SIGNS_ORDER = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  const moonSignIndex = SIGNS_ORDER.indexOf(moonSign);
  const saturnSignIndex = SIGNS_ORDER.indexOf(saturnCurrentSign);

  const signDiff = ((saturnSignIndex - moonSignIndex + 12) % 12);

  if (signDiff === 11 || signDiff === 0 || signDiff === 1) {
    sadeSatiStatus = '⚠️ Currently in or near Sade Sati (7.5 year Saturn transit). A challenging but transformative period. Stay patient and hardworking.';
    sadeSatiStatusTe = '⚠️ ప్రస్తుతం సాడే సాతి (7.5 సంవత్సరాల శని సంచారం)లో ఉన్నారు. సవాలుగా కానీ పరివర్తన కాలం. ఓపిక, కష్టపడటం ముఖ్యం.';
  } else if (signDiff === 3 || signDiff === 6 || signDiff === 10) {
    sadeSatiStatus = '✓ Saturn is in a favorable position. Good time for career growth and financial gains.';
    sadeSatiStatusTe = '✓ శని అనుకూల స్థానంలో ఉన్నాడు. వృత్తి వృద్ధి, ఆర్థిక లాభాలకు మంచి సమయం.';
  } else {
    sadeSatiStatus = '○ Saturn transit is neutral. Focus on consistent efforts for best results.';
    sadeSatiStatusTe = '○ శని సంచారం తటస్థంగా ఉంది. ఉత్తమ ఫలితాల కోసం స్థిరమైన ప్రయత్నాలపై దృష్టి పెట్టండి.';
  }

  // Jupiter transit benefit
  let jupiterTransit = '';
  let jupiterTransitTe = '';
  const jupiterCurrentSign = 'Taurus'; // Approximate for 2024-2025
  const jupiterSignIndex = SIGNS_ORDER.indexOf(jupiterCurrentSign);
  const jupiterDiff = ((jupiterSignIndex - moonSignIndex + 12) % 12);

  if (jupiterDiff === 0 || jupiterDiff === 4 || jupiterDiff === 8) {
    jupiterTransit = '🌟 Jupiter is blessing your chart. Excellent for education, marriage, and spiritual growth.';
    jupiterTransitTe = '🌟 బృహస్పతి మీ జాతకాన్ని ఆశీర్వదిస్తున్నాడు. విద్య, వివాహం, ఆధ్యాత్మిక వృద్ధికి అద్భుతం.';
  } else if (jupiterDiff === 2 || jupiterDiff === 5 || jupiterDiff === 9 || jupiterDiff === 11) {
    jupiterTransit = '✓ Jupiter transit is supportive. Good period for new ventures and relationships.';
    jupiterTransitTe = '✓ బృహస్పతి సంచారం సహాయకంగా ఉంది. కొత్త ప్రయత్నాలు, సంబంధాలకు మంచి సమయం.';
  } else {
    jupiterTransit = '○ Jupiter transit is neutral. Benefits may be delayed but will come.';
    jupiterTransitTe = '○ బృహస్పతి సంచారం తటస్థంగా ఉంది. ఫలితాలు ఆలస్యం కావచ్చు కానీ వస్తాయి.';
  }

  return {
    marriage: { en: marriageTiming, te: marriageTimingTe },
    career: { en: careerOutlook, te: careerOutlookTe },
    health: { en: healthConcerns, te: healthConcernsTe },
    saturnTransit: { en: sadeSatiStatus, te: sadeSatiStatusTe },
    jupiterTransit: { en: jupiterTransit, te: jupiterTransitTe },
    seventhLord,
    tenthLord,
    sixthLord,
  };
};

/**
 * Calculate all Doshas (afflictions) in a horoscope
 * Returns a comprehensive summary of doshas present
 * @param {Array} planetPositions - Array of planet position objects
 * @param {number} lagnaSign - Ascendant sign index (0-11)
 * @param {Object} janmaDetails - Nakshatra/Rashi details
 * @returns {Object} Dosha summary
 */
export const calculateDoshaSummary = (planetPositions, lagnaSign = 0, janmaDetails = null) => {
  if (!planetPositions || planetPositions.length === 0) return null;

  const getPlanet = (name) => planetPositions.find(p => p.name === name) || {};
  const doshas = [];

  const sun = getPlanet('Sun');
  const moon = getPlanet('Moon');
  const mars = getPlanet('Mars');
  const mercury = getPlanet('Mercury');
  const jupiter = getPlanet('Jupiter');
  const venus = getPlanet('Venus');
  const saturn = getPlanet('Saturn');
  const rahu = getPlanet('Rahu');
  const ketu = getPlanet('Ketu');

  // Helper to get house from lagna (always recalculate from sign relative to ascendant)
  const getHouse = (planet) => {
    if (!planet) return -1;
    const signIndex = planet.signIndex ?? SIGNS.indexOf(planet.sign);
    if (signIndex === -1 || signIndex === undefined) return -1;
    return ((signIndex - lagnaSign + 12) % 12) + 1;
  };

  const marsHouse = getHouse(mars);
  const saturnHouse = getHouse(saturn);
  const rahuHouse = getHouse(rahu);
  const ketuHouse = getHouse(ketu);
  const sunHouse = getHouse(sun);
  const moonHouse = getHouse(moon);
  const jupiterHouse = getHouse(jupiter);
  const venusHouse = getHouse(venus);

  // ============ 1. MANGLIK DOSHA ============
  const manglikResult = calculateManglikDosha(planetPositions, lagnaSign);
  if (manglikResult.isManglik) {
    doshas.push({
      name: 'Manglik Dosha',
      nameTe: 'మంగళ దోషం',
      present: true,
      severity: manglikResult.level, // Low, Medium, High, Cancelled
      severityTe: manglikResult.level === 'High' ? 'తీవ్రం' : manglikResult.level === 'Medium' ? 'మధ్యస్థం' : manglikResult.level === 'Cancelled' ? 'రద్దు' : 'తక్కువ',
      icon: '♂',
      color: manglikResult.level === 'Cancelled' ? '#4CAF50' : manglikResult.level === 'High' ? '#f44336' : '#FF9800',
      description: `Mars is in ${marsHouse}${marsHouse === 1 ? 'st' : marsHouse === 2 ? 'nd' : marsHouse === 3 ? 'rd' : 'th'} house. May affect marriage compatibility.`,
      descriptionTe: `కుజుడు ${marsHouse}వ భావంలో ఉన్నాడు. వివాహ అనుకూలతను ప్రభావితం చేయవచ్చు.`,
      remedy: 'Chant "Om Angarakaya Namaha" 108 times on Tuesdays. Wear Red Coral after consultation.',
      remedyTe: 'మంగళవారాలు "ఓం అంగారకాయ నమః" 108 సార్లు జపించండి. సంప్రదింపు తర్వాత పగడం ధరించండి.',
    });
  }

  // ============ 2. KAAL SARP DOSHA ============
  // All planets between Rahu and Ketu
  const rahuLong = rahu.longitude || 0;
  const ketuLong = ketu.longitude || 180;

  let allBetweenRahuKetu = true;
  let allBetweenKetuRahu = true;

  const otherPlanets = [sun, moon, mars, mercury, jupiter, venus, saturn];

  otherPlanets.forEach(planet => {
    if (!planet.longitude && planet.longitude !== 0) return;
    const pLong = planet.longitude;

    // Check if between Rahu and Ketu (going forward)
    if (rahuLong < ketuLong) {
      if (!(pLong > rahuLong && pLong < ketuLong)) {
        allBetweenRahuKetu = false;
      }
      if (!(pLong > ketuLong || pLong < rahuLong)) {
        allBetweenKetuRahu = false;
      }
    } else {
      if (!(pLong > rahuLong || pLong < ketuLong)) {
        allBetweenRahuKetu = false;
      }
      if (!(pLong > ketuLong && pLong < rahuLong)) {
        allBetweenKetuRahu = false;
      }
    }
  });

  const isKaalSarp = allBetweenRahuKetu || allBetweenKetuRahu;
  const isPartialKaalSarp = !isKaalSarp && (
    otherPlanets.filter(p => {
      if (!p.longitude && p.longitude !== 0) return false;
      const pLong = p.longitude;
      if (rahuLong < ketuLong) {
        return pLong > rahuLong && pLong < ketuLong;
      } else {
        return pLong > rahuLong || pLong < ketuLong;
      }
    }).length >= 5
  );

  if (isKaalSarp) {
    doshas.push({
      name: 'Kaal Sarp Dosha',
      nameTe: 'కాల సర్ప దోషం',
      present: true,
      severity: 'High',
      severityTe: 'తీవ్రం',
      icon: '🐍',
      color: '#9C27B0',
      description: 'All planets are hemmed between Rahu and Ketu. May cause delays and obstacles in life.',
      descriptionTe: 'అన్ని గ్రహాలు రాహు-కేతువుల మధ్య ఉన్నాయి. జీవితంలో ఆలస్యాలు, అడ్డంకులు కలిగించవచ్చు.',
      remedy: 'Visit Srikalahasti or Trimbakeshwar temple. Perform Kaal Sarp Shanti puja.',
      remedyTe: 'శ్రీకాళహస్తి లేదా త్రయంబకేశ్వర్ దేవాలయాన్ని సందర్శించండి. కాల సర్ప శాంతి పూజ చేయించండి.',
    });
  } else if (isPartialKaalSarp) {
    doshas.push({
      name: 'Partial Kaal Sarp',
      nameTe: 'పాక్షిక కాల సర్ప దోషం',
      present: true,
      severity: 'Medium',
      severityTe: 'మధ్యస్థం',
      icon: '🐍',
      color: '#FF9800',
      description: 'Most planets are between Rahu-Ketu. Some obstacles possible but manageable.',
      descriptionTe: 'చాలా గ్రహాలు రాహు-కేతువుల మధ్య ఉన్నాయి. కొన్ని అడ్డంకులు సాధ్యం కానీ నిర్వహించగలవు.',
      remedy: 'Chant "Om Namah Shivaya" daily. Visit Shiva temple on Mondays.',
      remedyTe: 'రోజూ "ఓం నమః శివాయ" జపించండి. సోమవారాలు శివ దేవాలయాన్ని సందర్శించండి.',
    });
  }

  // ============ 3. PITRA DOSHA ============
  // Sun afflicted by Rahu/Ketu or Saturn in 9th house
  const sunWithRahu = sunHouse === rahuHouse || Math.abs((sun.longitude || 0) - (rahu.longitude || 0)) < 15;
  const sunWithKetu = sunHouse === ketuHouse || Math.abs((sun.longitude || 0) - (ketu.longitude || 0)) < 15;
  const saturnIn9th = saturnHouse === 9;
  const rahuIn9th = rahuHouse === 9;

  if (sunWithRahu || sunWithKetu || saturnIn9th || rahuIn9th) {
    doshas.push({
      name: 'Pitra Dosha',
      nameTe: 'పితృ దోషం',
      present: true,
      severity: (sunWithRahu || saturnIn9th) ? 'Medium' : 'Low',
      severityTe: (sunWithRahu || saturnIn9th) ? 'మధ్యస్థం' : 'తక్కువ',
      icon: '🙏',
      color: '#795548',
      description: 'Ancestral karmic debt indicated. May face obstacles from unknown sources.',
      descriptionTe: 'పూర్వీకుల కర్మ ఋణం సూచించబడింది. తెలియని మూలాల నుండి అడ్డంకులు ఎదుర్కోవచ్చు.',
      remedy: 'Perform Tarpan/Shraddh for ancestors. Feed crows and Brahmins on Amavasya.',
      remedyTe: 'పూర్వీకులకు తర్పణ/శ్రాద్ధం చేయండి. అమావాస్య రోజు కాకులకు, బ్రాహ్మణులకు ఆహారం పెట్టండి.',
    });
  }

  // ============ 4. GRAHAN DOSHA ============
  // Sun or Moon conjunct Rahu/Ketu
  const sunGrahan = Math.abs((sun.longitude || 0) - (rahu.longitude || 0)) < 10 ||
                    Math.abs((sun.longitude || 0) - (ketu.longitude || 0)) < 10;
  const moonGrahan = Math.abs((moon.longitude || 0) - (rahu.longitude || 0)) < 10 ||
                     Math.abs((moon.longitude || 0) - (ketu.longitude || 0)) < 10;

  if (sunGrahan || moonGrahan) {
    doshas.push({
      name: 'Grahan Dosha',
      nameTe: 'గ్రహణ దోషం',
      present: true,
      severity: (sunGrahan && moonGrahan) ? 'High' : 'Medium',
      severityTe: (sunGrahan && moonGrahan) ? 'తీవ్రం' : 'మధ్యస్థం',
      icon: '🌑',
      color: '#37474F',
      description: `${sunGrahan ? 'Sun' : ''}${sunGrahan && moonGrahan ? ' and ' : ''}${moonGrahan ? 'Moon' : ''} eclipsed by Rahu/Ketu. May affect ${sunGrahan ? 'father, authority' : ''}${sunGrahan && moonGrahan ? ' and ' : ''}${moonGrahan ? 'mother, mind' : ''}.`,
      descriptionTe: `${sunGrahan ? 'సూర్యుడు' : ''}${sunGrahan && moonGrahan ? ' మరియు ' : ''}${moonGrahan ? 'చంద్రుడు' : ''} రాహు/కేతువులతో గ్రహణం. ${sunGrahan ? 'తండ్రి, అధికారం' : ''}${sunGrahan && moonGrahan ? ' మరియు ' : ''}${moonGrahan ? 'తల్లి, మనసు' : ''} ప్రభావితం కావచ్చు.`,
      remedy: 'Donate black sesame seeds. Chant Rahu/Ketu mantras. Observe fasts on eclipse days.',
      remedyTe: 'నల్ల నువ్వులు దానం చేయండి. రాహు/కేతు మంత్రాలు జపించండి. గ్రహణ రోజులలో ఉపవాసం ఉండండి.',
    });
  }

  // ============ 5. SHANI DOSHA (Saturn affliction) ============
  // Saturn in 1st, 4th, 7th, 8th, or 10th causing issues
  const shaniDoshaHouses = [1, 4, 7, 8];
  if (shaniDoshaHouses.includes(saturnHouse)) {
    doshas.push({
      name: 'Shani Dosha',
      nameTe: 'శని దోషం',
      present: true,
      severity: saturnHouse === 8 ? 'High' : 'Medium',
      severityTe: saturnHouse === 8 ? 'తీవ్రం' : 'మధ్యస్థం',
      icon: '♄',
      color: '#3F51B5',
      description: `Saturn in ${saturnHouse}${saturnHouse === 1 ? 'st' : saturnHouse === 2 ? 'nd' : saturnHouse === 3 ? 'rd' : 'th'} house. May cause delays, hard work, and late success.`,
      descriptionTe: `శని ${saturnHouse}వ భావంలో ఉన్నాడు. ఆలస్యాలు, కష్టపడటం, ఆలస్యంగా విజయం కలిగించవచ్చు.`,
      remedy: 'Light sesame oil lamp on Saturdays. Donate black items. Chant "Om Sham Shanaischaraya Namaha".',
      remedyTe: 'శనివారాలు నువ్వుల నూనె దీపం వెలిగించండి. నల్ల వస్తువులు దానం చేయండి. "ఓం శం శనైశ్చరాయ నమః" జపించండి.',
    });
  }

  // ============ 6. KEMDRUM DOSHA ============
  // Moon has no planets on either side (2nd and 12th from Moon)
  const moonSignIndex = SIGNS.indexOf(moon.sign);
  const secondFromMoon = SIGNS[(moonSignIndex + 1) % 12];
  const twelfthFromMoon = SIGNS[(moonSignIndex + 11) % 12];

  const planetsIn2nd = planetPositions.filter(p => p.sign === secondFromMoon && !['Rahu', 'Ketu'].includes(p.name));
  const planetsIn12th = planetPositions.filter(p => p.sign === twelfthFromMoon && !['Rahu', 'Ketu'].includes(p.name));

  const isKemdrum = planetsIn2nd.length === 0 && planetsIn12th.length === 0;

  // Check for cancellation
  const kemdrumCancelled = jupiterHouse === 1 || jupiterHouse === 4 || jupiterHouse === 7 || jupiterHouse === 10 ||
                           moonHouse === 1 || moonHouse === 4 || moonHouse === 7 || moonHouse === 10;

  if (isKemdrum && !kemdrumCancelled) {
    doshas.push({
      name: 'Kemdrum Dosha',
      nameTe: 'కేంద్రుమ దోషం',
      present: true,
      severity: 'Medium',
      severityTe: 'మధ్యస్థం',
      icon: '☽',
      color: '#607D8B',
      description: 'Moon is isolated without planetary support. May face financial instability or loneliness at times.',
      descriptionTe: 'చంద్రుడు గ్రహ మద్దతు లేకుండా ఒంటరిగా ఉన్నాడు. కొన్నిసార్లు ఆర్థిక అస్థిరత లేదా ఒంటరితనం ఎదుర్కోవచ్చు.',
      remedy: 'Worship Lord Shiva on Mondays. Wear Pearl after consultation. Keep fast on full moon days.',
      remedyTe: 'సోమవారాలు శివుడిని పూజించండి. సంప్రదింపు తర్వాత ముత్యం ధరించండి. పౌర్ణమి రోజులలో ఉపవాసం ఉండండి.',
    });
  }

  // ============ 7. GURU CHANDAL DOSHA ============
  // Jupiter conjunct Rahu
  const jupiterWithRahu = jupiterHouse === rahuHouse || Math.abs((jupiter.longitude || 0) - (rahu.longitude || 0)) < 15;

  if (jupiterWithRahu) {
    doshas.push({
      name: 'Guru Chandal Dosha',
      nameTe: 'గురు చాండాల దోషం',
      present: true,
      severity: 'Medium',
      severityTe: 'మధ్యస్థం',
      icon: '♃',
      color: '#E91E63',
      description: 'Jupiter afflicted by Rahu. May cause confusion in decisions, issues with teachers/mentors.',
      descriptionTe: 'రాహువు వల్ల గురువు బాధితుడయ్యాడు. నిర్ణయాలలో గందరగోళం, గురువులు/మార్గదర్శకులతో సమస్యలు కలిగించవచ్చు.',
      remedy: 'Respect teachers and elders. Donate yellow items on Thursdays. Chant Vishnu Sahasranama.',
      remedyTe: 'గురువులు, పెద్దలను గౌరవించండి. గురువారాలు పసుపు వస్తువులు దానం చేయండి. విష్ణు సహస్రనామం జపించండి.',
    });
  }

  // ============ 8. NADI DOSHA (for marriage context) ============
  // Based on Nakshatra - check Nadi type
  if (janmaDetails?.nakshatra) {
    const ADI_NADI_STARS = ['Ashwini', 'Ardra', 'Punarvasu', 'Uttara Phalguni', 'Hasta', 'Jyeshtha', 'Mula', 'Shatabhisha', 'Purva Bhadrapada'];
    const MADHYA_NADI_STARS = ['Bharani', 'Mrigashira', 'Pushya', 'Purva Phalguni', 'Chitra', 'Anuradha', 'Purva Ashadha', 'Dhanishta', 'Uttara Bhadrapada'];
    const ANTYA_NADI_STARS = ['Krittika', 'Rohini', 'Ashlesha', 'Magha', 'Swati', 'Vishakha', 'Uttara Ashadha', 'Shravana', 'Revati'];

    let nadiType = '';
    let nadiTypeTe = '';
    if (ADI_NADI_STARS.includes(janmaDetails.nakshatra.name)) {
      nadiType = 'Adi (Vata)';
      nadiTypeTe = 'ఆది (వాత)';
    } else if (MADHYA_NADI_STARS.includes(janmaDetails.nakshatra.name)) {
      nadiType = 'Madhya (Pitta)';
      nadiTypeTe = 'మధ్య (పిత్త)';
    } else if (ANTYA_NADI_STARS.includes(janmaDetails.nakshatra.name)) {
      nadiType = 'Antya (Kapha)';
      nadiTypeTe = 'అంత్య (కఫ)';
    }

    // Note: Nadi dosha only applies when same nadi marries same nadi
    // We just display the nadi type for awareness
    doshas.push({
      name: 'Nadi Type',
      nameTe: 'నాడి రకం',
      present: false, // Not a dosha, just info
      isInfo: true,
      severity: nadiType,
      severityTe: nadiTypeTe,
      icon: '🩺',
      color: '#009688',
      description: `Your Nadi is ${nadiType}. Avoid marrying same Nadi for progeny wellness.`,
      descriptionTe: `మీ నాడి ${nadiTypeTe}. సంతాన ఆరోగ్యం కోసం అదే నాడి వారిని వివాహం చేసుకోకండి.`,
      remedy: 'Match Nadi during horoscope matching. Different Nadi types are compatible.',
      remedyTe: 'జాతక మ్యాచింగ్ సమయంలో నాడి పరిశీలించండి. వేర్వేరు నాడి రకాలు అనుకూలం.',
    });
  }

  // Calculate overall dosha score
  const actualDoshas = doshas.filter(d => d.present && !d.isInfo);
  const highSeverity = actualDoshas.filter(d => d.severity === 'High').length;
  const mediumSeverity = actualDoshas.filter(d => d.severity === 'Medium').length;
  const lowSeverity = actualDoshas.filter(d => d.severity === 'Low' || d.severity === 'Cancelled').length;

  let overallStatus = 'Clean';
  let overallStatusTe = 'శుద్ధం';
  let overallColor = '#4CAF50';

  if (highSeverity >= 2) {
    overallStatus = 'Needs Attention';
    overallStatusTe = 'శ్రద్ధ అవసరం';
    overallColor = '#f44336';
  } else if (highSeverity === 1 || mediumSeverity >= 2) {
    overallStatus = 'Minor Afflictions';
    overallStatusTe = 'చిన్న దోషాలు';
    overallColor = '#FF9800';
  } else if (mediumSeverity === 1 || lowSeverity >= 2) {
    overallStatus = 'Mostly Clear';
    overallStatusTe = 'చాలావరకు శుద్ధం';
    overallColor = '#8BC34A';
  }

  return {
    doshas,
    summary: {
      total: actualDoshas.length,
      high: highSeverity,
      medium: mediumSeverity,
      low: lowSeverity,
      overallStatus,
      overallStatusTe,
      overallColor,
    },
    noDoshas: actualDoshas.length === 0,
  };
};
